import { Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { Motion, Presence } from "../components/presence";
import { t } from "../i18n/i18n";
import { TRIAL_EXPORT_MESSAGE, TRIAL_TIP_EVENT } from "./trial";

/** Bulle « Export impossible avec une licence d'essai. » au survol (ou au focus clavier) de tout geste d'export
 *  grisé (demande d'Alexis du 30/10/2026) : la bulle native du `title` arrivait tard, sans le style de l'application.
 *  Un seul hôte pour toute l'application : il repère le geste à son `title` (posé par `trialBlock`), le retire le
 *  temps du survol pour éviter la double bulle, puis le rend. À l'appui (souris ou doigt), `explainTrialExport` la montre
 *  contre le geste pendant trois secondes, jamais en bas de l'écran. */

// Au-dessus des menus ouverts (z-100), d'où partent la plupart des gestes d'export.
const GAP = 8;
const WIDTH = 260;

interface Tip {
  left: number;
  top: number;
  below: boolean;
}

function blockedTarget(node: EventTarget | null, message: string): HTMLElement | null {
  let el = node instanceof Element ? (node as HTMLElement) : null;
  while (el) {
    // Titre exact, ou suivi du libellé (barre latérale repliée : « Exports · Export impossible… »).
    const title = el.getAttribute("title") ?? el.dataset.trialTip ?? "";
    if (title === message || title.endsWith(` · ${message}`)) return el;
    el = el.parentElement;
  }
  return null;
}

function place(el: HTMLElement): Tip {
  const rect = el.getBoundingClientRect();
  const left = Math.max(8, Math.min(rect.left + rect.width / 2 - WIDTH / 2, window.innerWidth - WIDTH - 8));
  const below = rect.top < 56;
  return { left, top: below ? rect.bottom + GAP : rect.top - GAP, below };
}

export function TrialTipHost() {
  const [tip, setTip] = useState<Tip | null>(null);

  useEffect(() => {
    const message = t(TRIAL_EXPORT_MESSAGE);
    let current: HTMLElement | null = null;
    const release = () => {
      if (current?.dataset.trialTip !== undefined) {
        current.setAttribute("title", current.dataset.trialTip);
        delete current.dataset.trialTip;
      }
      current = null;
      setTip(null);
    };
    let timer: number | undefined;
    const showAt = (el: HTMLElement) => {
      if (el === current) return;
      release();
      current = el;
      const title = el.getAttribute("title");
      if (title) {
        el.dataset.trialTip = title;
        el.removeAttribute("title");
      }
      setTip(place(el));
    };
    const show = (event: Event) => {
      const el = blockedTarget(event.target, message);
      if (el) showAt(el);
    };
    // Appui (souris ou doigt) : la bulle contre le geste, quelques secondes (au doigt, aucun survol ne la montre).
    const pressed = (event: Event) => {
      const el = (event as CustomEvent<HTMLElement>).detail;
      if (!(el instanceof HTMLElement)) return;
      window.clearTimeout(timer);
      if (el !== current) showAt(el);
      timer = window.setTimeout(release, 3000);
    };
    const leave = (event: Event) => {
      if (!current) return;
      const next = (event as MouseEvent | FocusEvent).relatedTarget;
      if (next instanceof Node && current.contains(next)) return;
      release();
    };
    window.addEventListener(TRIAL_TIP_EVENT, pressed);
    document.addEventListener("pointerover", show, true);
    document.addEventListener("focusin", show, true);
    document.addEventListener("pointerout", leave, true);
    document.addEventListener("focusout", leave, true);
    window.addEventListener("scroll", release, true);
    window.addEventListener("resize", release);
    return () => {
      document.removeEventListener("pointerover", show, true);
      document.removeEventListener("focusin", show, true);
      document.removeEventListener("pointerout", leave, true);
      document.removeEventListener("focusout", leave, true);
      window.removeEventListener("scroll", release, true);
      window.removeEventListener("resize", release);
      window.removeEventListener(TRIAL_TIP_EVENT, pressed);
      window.clearTimeout(timer);
      release();
    };
  }, []);

  if (typeof document === "undefined") return null;
  return createPortal(
    <Presence show={tip !== null}>
      {tip && (
        // Placement sur un conteneur fixe, animation sur la bulle : la translation de placement et celle de
        // l'apparition ne se contrarient pas.
        <div
          style={{
            position: "fixed",
            left: tip.left,
            top: tip.top,
            width: WIDTH,
            transform: tip.below ? undefined : "translateY(-100%)",
          }}
          className="pointer-events-none z-[110]"
        >
          <Motion
            origin={tip.below ? "top" : "bottom"}
            role="tooltip"
            data-trial-tip-bubble=""
            className="flex items-start gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-meta leading-snug text-ink shadow-float"
          >
            <Lock size={13} className="mt-px shrink-0 text-ink-muted" aria-hidden />
            <span>{t(TRIAL_EXPORT_MESSAGE)}</span>
          </Motion>
        </div>
      )}
    </Presence>,
    document.body,
  );
}
